import { formatToPercentage } from '@pequity/shared/ccp/config/ccp-custom.helpers';
import {
  BACKEND_DATA_INPUT_TYPE_MAP,
  type DisplayLayout,
  type Employee,
  type Field,
  GROUP_VIEW_EXCLUDED_FIELDS,
  type GroupViewExcludedField,
  LINKED_FIELDS,
  type LinkedFieldKey,
  type MetaKey,
} from '@pequity/shared/ccp/types';
import { CURRENCY_DISPLAY_OPTIONS, type CurrencyDisplay, DEFAULT_CURRENCY } from '@pequity/shared/ccp/types/constants';
import { createFieldPlaceholder, getRecommendedValue } from '@pequity/shared/ccp/utils/common';
import { formatToCurrency } from '@pequity/shared/ccp/utils/currency';
import { CURRENCY_INPUT_DEFAULTS } from '@pequity/shared/ui';
import { toNumberOrNull } from '@pequity/shared/utils/number';
import { toString } from '@pequity/shared/utils/string';
import { cloneDeep } from 'lodash-es';
import type { Simplify } from 'type-fest';
import { useI18n } from 'vue-i18n';
import type { AnySchema } from 'yup';
import * as yup from 'yup';

type ValidationContext = {
  min?: number;
  max?: number;
  minFormatted?: string;
  maxFormatted?: string;
};

const FIELD_TYPES_WITH_MIN_MAX_VALIDATION: readonly Field['fieldType'][] = [
  'inputNumber',
  'inputPercent',
  'inputCurrency',
  'inputCurrencyInfo',
] as const;

const FIELD_TYPES_WITH_STRING_VALIDATION: readonly Field['fieldType'][] = ['inputText', 'inputTextLong'] as const;

const MIN_MAX_CURRENCY_FORMAT_FIELD_TYPES: readonly Field['fieldType'][] = [
  'inputCurrency',
  'inputCurrencyInfo',
] as const;

export const SELECT_FIELD_TYPES: readonly Field['fieldType'][] = ['inputSelectText', 'inputBoolean'] as const;

const metaSchema = yup
  .object()
  .nullable()
  .noUnknown()
  .shape({
    min: yup.number().positive().nullable(),
    max: yup.number().positive().nullable(),
    round_to: yup.number().positive().nullable(),
    decimal_precision: yup.number().positive().integer().nullable(),
    currency: yup.string().nullable(),
    currency_display: yup.string().oneOf(CURRENCY_DISPLAY_OPTIONS).nullable(),
  } satisfies { [key in MetaKey]: AnySchema });

export type Meta = Simplify<
  yup.InferType<typeof metaSchema> & { currency_display?: null | undefined | CurrencyDisplay }
>;

const getFieldType = (displayLayout: DisplayLayout): Field['fieldType'] => {
  // First check if backend provides field_type override
  if (displayLayout.field_type) {
    return displayLayout.field_type as Field['fieldType'];
  }

  // Otherwise use standard type mapping
  return displayLayout.is_list === true
    ? 'inputSelectText'
    : BACKEND_DATA_INPUT_TYPE_MAP[displayLayout.type] || 'inputText';
};

/**
 * Returns a flat array that contains ALL the fields (including compound fields).
 * We then "v-bind" these fields' props with the actual fields in the template.
 */
const createFields = (displayLayouts: DisplayLayout[]): Field[] => {
  // Extract all compound layout fields
  const compoundDisplayLayouts = displayLayouts.reduce<DisplayLayout[]>((acc, curr) => {
    return [...acc, ...curr.compound_layouts];
  }, []);

  const backendFields = displayLayouts.concat(compoundDisplayLayouts).map((displayLayout) => {
    const backendField: Field = {
      displayLayoutId: displayLayout.id,
      data_name: displayLayout.data_name,
      name: displayLayout.name,
      min: displayLayout.min,
      max: displayLayout.max,
      label: displayLayout.display_name,
      hidden: displayLayout.hide_in_edit_form,
      required: displayLayout.required ?? false,
      disabled: !displayLayout.editable,
      validation: null,
      maxlength: displayLayout.max_length ?? null,
      fieldType: getFieldType(displayLayout),
      roundTo: displayLayout.round_to,
      requiredJustification: displayLayout.required_justification,
      precision: displayLayout.decimal_precision || CURRENCY_INPUT_DEFAULTS.precision,
      placeholder: '',
      recommendedValue: '',
      timezone: '',
      searchable: displayLayout.is_list === true || displayLayout.type === 'wave',
      valueIsObject: displayLayout.is_list === true || displayLayout.type === 'wave',
      currency: '',
      currencyDisplay: displayLayout.currency_display,
      compoundLayouts:
        displayLayout.compound_layouts?.map((compoundLayout) => ({
          displayLayoutId: compoundLayout.id,
          name: compoundLayout.name,
          fieldType: getFieldType(compoundLayout),
        })) ?? [],
    };

    // Override validation for list fields - they don't use standard validation
    if (displayLayout.is_list === true) {
      backendField.validation = null;
    }

    return backendField;
  });

  // All fields now come from backend (including compound children)
  return backendFields;
};

/**
 * Removes unneeded attributes from the meta object
 */
const castMeta = (meta: Meta) => {
  // validateSync throws an error if it fails
  metaSchema.validateSync(meta);

  const res = metaSchema.cast(meta);

  return res;
};

/**
 * Creates field attributes(precision, currency, rounding)
 */
const createFieldAttributes = (field: Field, employeeDataMeta: Record<string, Meta>) => {
  const meta = castMeta(employeeDataMeta[field.name]);

  const res = {
    precision: meta?.decimal_precision ?? field.precision,
    currency:
      field.fieldType === 'inputCurrency' || field.fieldType === 'inputCurrencyInfo'
        ? (meta?.currency ?? DEFAULT_CURRENCY)
        : null,
    currencyDisplay: meta?.currency_display ?? field.currencyDisplay,
    roundTo: meta?.round_to ?? field.roundTo ?? null,
  };

  return res;
};

/**
 * Creates field attributes for composite fields (e.g Employee Name, Range, etc)
 */
const createCompositeFieldAttributes = (
  field: Field,
  meta: Meta,
  employeeData: Employee | Record<string, never>,
  currencyFallback?: string | null
) => {
  const recommendedValue = getRecommendedValue(employeeData, field.name);
  const currency = (field.currency = meta?.currency ?? currencyFallback ?? DEFAULT_CURRENCY);
  const currencyDisplay = meta?.currency_display ?? field.currencyDisplay;

  const res = {
    name: field.data_name,
    roundTo: meta?.round_to ?? field.roundTo ?? null,
    precision: meta?.decimal_precision ?? field.precision,
    currency,
    currencyDisplay,
    placeholder: createFieldPlaceholder(
      field.fieldType,
      Number(recommendedValue),
      currency,
      field.precision,
      currencyDisplay
    ),
    recommendedValue,
  };

  return res;
};

/**
 * Creates field attributes for linked fields (e.g salary_increase_recommended_override, annual_bonus_recommended_override, etc)
 */
const createLinkedFieldAttributes = (field: Field, fields: Field[], meta: Meta) => {
  const res = {
    roundTo:
      meta?.round_to ??
      resolveLinkedFieldAttributes('roundTo', LINKED_FIELDS[field.name as LinkedFieldKey], fields) ??
      field.roundTo,
    precision:
      meta?.decimal_precision ??
      resolveLinkedFieldAttributes('precision', LINKED_FIELDS[field.name as LinkedFieldKey], fields) ??
      field.precision,
    currency: meta?.currency ?? DEFAULT_CURRENCY,
    currencyDisplay: meta?.currency_display ?? field.currencyDisplay,
  };

  return res;
};

/**
 * Creates the fields array to be used inside the field edit dropdown form
 *
 * Simple logic:
 * 1. Find the main field by fieldName (database name)
 * 2. Add field attributes for validation/display
 * 3. If it has compound children, include them too
 * 4. Return all fields to render
 */
export const createFormFields = (
  fieldName: string,
  fields: Field[],
  employeeData: Employee | Record<string, never>
): Field[] => {
  const res = cloneDeep(fields);

  // Special case: linked fields (e.g., range_salary_low points to "Range")
  const linkedFieldName = LINKED_FIELDS[fieldName as LinkedFieldKey];
  if (linkedFieldName) {
    const linkedField = res.find((field) => field.name === linkedFieldName);
    if (linkedField) {
      const meta = castMeta(employeeData.meta[linkedFieldName]) || {};
      const fieldAttributes = createLinkedFieldAttributes(linkedField, res, meta);
      return [{ ...linkedField, ...fieldAttributes }];
    }
  }

  // Find the main field by database name
  let mainField = res.find((field) => field.name === fieldName);

  if (!mainField) {
    return [];
  }

  // Check if it's a compound field - if so, render all compound children
  if (mainField.compoundLayouts.length > 0) {
    // Add field attributes with recommended values for compound fields
    const mainMeta = castMeta(employeeData.meta[mainField.name]) || {};
    mainField = { ...mainField, ...createCompositeFieldAttributes(mainField, mainMeta, employeeData) };
    const compoundCurrency = MIN_MAX_CURRENCY_FORMAT_FIELD_TYPES.includes(mainField.fieldType)
      ? mainField.currency
      : null;

    const compoundFields = mainField.compoundLayouts
      .map((compoundLayout) => {
        const compoundField = res.find((field) => field.name === compoundLayout.name);
        if (!compoundField) {
          return null;
        }
        const meta = castMeta(employeeData.meta[compoundField.name]) || {};
        const fieldAttributes = createCompositeFieldAttributes(compoundField, meta, employeeData, compoundCurrency);
        return { ...compoundField, ...fieldAttributes } as Field;
      })
      .filter((field): field is Field => field !== null);

    return [mainField, ...compoundFields];
  }

  // Regular field - just add basic attributes
  mainField = { ...mainField, ...createFieldAttributes(mainField, employeeData.meta) };
  return [mainField];
};

const resolveLinkedFieldAttributes = <T extends keyof Field>(
  attr: T,
  fieldName: string,
  fields: Field[]
): Field[T] | undefined => {
  const field = fields.find((field) => field.name === fieldName);
  return field ? field[attr] : undefined;
};

export const createAdminFields = (backendFields: DisplayLayout[]) => {
  const adminFields = createFields(backendFields).map((field) => {
    return { ...field, requiredJustification: false };
  });

  return adminFields;
};

export const createGroupFields = (backendFields: DisplayLayout[]) => {
  const groupFields = createFields(backendFields).filter(
    (field) => !GROUP_VIEW_EXCLUDED_FIELDS.includes(field.name as GroupViewExcludedField)
  );

  return groupFields;
};

export const useCreateValidationSchema = () => {
  const { t } = useI18n();

  const messageNotNumber = t('app.validation_invalid_number');
  const fieldIsRequired = t('app.validation_required');

  // The nullableNumberRule rule does not work with peq-input-currency because:
  // peq-input-currency returns NaN in case the value is a string or empty
  const nullableNumberRule = yup.number().nullable().typeError(messageNotNumber);
  const requiredNumberRule = yup.number().required(fieldIsRequired).typeError(fieldIsRequired);
  const nullableStringRule = yup.string().nullable();
  const requiredStringRule = yup.string().nullable().required(fieldIsRequired);

  const createValidationSchema = (fields: Field[]) => {
    const yupObjectShape = fields.reduce((acc, field) => {
      // Disabled fields should not be validated
      if (field.disabled) {
        return acc;
      }

      let rule = field.validation;

      if (!rule) {
        if (FIELD_TYPES_WITH_MIN_MAX_VALIDATION.includes(field.fieldType)) {
          const baseNumberRule = field.required ? requiredNumberRule : nullableNumberRule;

          rule = baseNumberRule
            .transform((currentValue) => {
              return toNumberOrNull(currentValue);
            })
            // https://github.com/jquense/yup#schemawhenkeys-string--string-builder-object--values-any-schema--schema-schema
            .when(`$${field.name}.min`, ([val], schema, { context }) => {
              const minFormatted = context[field.name]?.minFormatted;

              return val ? schema.min(val, t('app.validation_min_value', { value: minFormatted })) : schema;
            })
            .when(`$${field.name}.max`, ([val], schema, { context }) => {
              const maxFormatted = context[field.name]?.maxFormatted;

              return val ? schema.max(val, t('app.validation_max_value', { value: maxFormatted })) : schema;
            });
        }

        if (FIELD_TYPES_WITH_STRING_VALIDATION.includes(field.fieldType)) {
          rule = field.required ? requiredStringRule : nullableStringRule;
        }
      }

      return { ...acc, [field.name]: rule };
    }, {});

    return yup.object().shape(yupObjectShape);
  };

  return { createValidationSchema };
};

export const createValidationContext = (
  fields: Field[],
  rowMeta: Record<string, Meta>
): Record<string, ValidationContext> => {
  return fields.reduce((acc, field) => {
    const meta = rowMeta[field.name] || {};

    const min = meta?.min ?? field?.min;
    const max = meta?.max ?? field?.max;
    const precision = meta?.decimal_precision ?? field?.precision;

    // We need the formatted min/max values passed into the validation context so that we can display them in error messages
    let minFormatted = toString(min);
    let maxFormatted = toString(max);

    if (MIN_MAX_CURRENCY_FORMAT_FIELD_TYPES.includes(field.fieldType)) {
      const currency = meta?.currency ?? field.currency;
      minFormatted = toString(formatToCurrency(min, currency, precision));
      maxFormatted = toString(formatToCurrency(max, currency, precision));
    }

    if (field.fieldType === 'inputPercent') {
      minFormatted = toString(formatToPercentage(min, precision));
      maxFormatted = toString(formatToPercentage(max, precision));
    }

    const context = { min, max, minFormatted, maxFormatted };

    return { ...acc, [field.name]: context };
  }, {});
};
